import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as fabric from 'fabric';
import axios from 'axios';
import Swal from 'sweetalert2';
import './FeedbackWidget.css';

// FIX: Ensure correct access to the fabric library.
// The core object is usually fabric.fabric (UMD) or the default export (ESM).
const fabricCore = (fabric.fabric || fabric);

// Fallback safety check and explicit assignment
const Canvas = fabricCore.Canvas;
const FabricImage = fabricCore.Image || fabricCore.FabricImage; // Fabric.js V5+ often uses 'Image'
const Group = fabricCore.Group;
const IText = fabricCore.IText;
const Line = fabricCore.Line;
const Triangle = fabricCore.Triangle;

// Safety check for the missing class
if (!FabricImage) {
    console.error("CRITICAL ERROR: FabricImage class is not found in the imported Fabric.js library. Check Fabric.js version.");
}
// SVG Icons (Unchanged)
const iconSVG = {
    close: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
    feedback: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>',
    text: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>',
    arrow: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>',
    undo: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"></path></svg>',
    delete: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>',
    upload: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>'
};

export default function FeedbackWidget({ apiEndpoint = '/api/public/SendFeedBack', screenshotUrl = 'https://api.alphadezine.com/fullpage-ss', devUrl = 'https://localhost:7211' }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [attachment, setAttachment] = useState([]);
    const [screenshotReady, setScreenshotReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const fabricCanvasRef = useRef(null);
    const fileInputRef = useRef(null);

    const initializeCanvas = useCallback(() => {
        if (!canvasRef.current || !containerRef.current) return false;

        if (fabricCanvasRef.current) {
            fabricCanvasRef.current.dispose();
            fabricCanvasRef.current = null;
        }

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const canvasElement = canvasRef.current;

        // Safety check for dimensions
        if (rect.width <= 0 || rect.height <= 0) {
            console.warn('Canvas container dimensions are zero. Aborting initialization.');
            return false;
        }

        canvasElement.width = rect.width;
        canvasElement.height = rect.height;

        try {
            // Use the Canvas constructor destructured from fabricCore
            const fabricCanvas = new Canvas(canvasElement, {
                isDrawingMode: false,
                selection: true
            });

            fabricCanvas.setDimensions({
                width: rect.width,
                height: rect.height,
            });

            fabricCanvasRef.current = fabricCanvas;
            return true;
        } catch (error) {
            console.error('Failed to initialize canvas:', error);
            console.error('Canvas constructor status:', Canvas);
            return false;
        }
    }, []);

    const arrayBufferToBase64 = (buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    };

    const captureScreenshot = useCallback(async () => {
        try {
            const canvas = fabricCanvasRef.current;
            if (!canvas) {
                console.warn('Canvas not ready for screenshot capture.');
                return;
            }
            const response = await axios.post(screenshotUrl, {
                "url": window.location.href.replaceAll(`${devUrl}`, 'https://globalengagementsolutions-dev.azurewebsites.net'),
                "width": document.documentElement.clientWidth
            }, { responseType: 'arraybuffer' });

            // FIX: Use native browser functions to convert ArrayBuffer to Base64
            const base64String = arrayBufferToBase64(response.data);
            const base64 = `data:image/png;base64,${base64String}`;

            // Use FabricImage constructor destructured from fabricCore
            FabricImage.fromURL(base64, (img) => {
                const canvasWidth = canvas.getWidth() - 17;

                img.set({
                    selectable: false,
                    evented: false,
                });
                img.scaleToWidth(canvasWidth);
                // Set canvas height based on the scaled image height
                canvas.setHeight(img.getScaledHeight());
                canvas.set("backgroundImage", img);
                canvas.renderAll();
                setScreenshotReady(true);
            }, {
                crossOrigin: 'anonymous'
            });

        } catch (error) {
            console.error('Screenshot error:', error);
        }
    }, [screenshotUrl]);

    const setTool = (tool) => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        canvas.isDrawingMode = false;
        const center = {
            left: containerRef.current.getBoundingClientRect().width / 2,
            top: containerRef.current.getBoundingClientRect().height / 2,
        };

        switch (tool) {
            case 'text':
                const text = new IText('Text', {
                    left: center.left,
                    top: center.top,
                    originX: 'center',
                    originY: 'center',
                    fontSize: 24,
                    fill: '#000000',
                });
                canvas.add(text);
                canvas.setActiveObject(text);
                break;
            case 'arrow':
                const arrowLength = 100;
                const arrow = new Group([
                    new Line([0, 0, arrowLength, 0], {
                        stroke: '#000',
                        strokeWidth: 3,
                    }),
                    new Triangle({
                        left: arrowLength,
                        top: 0,
                        originX: 'center',
                        originY: 'center',
                        angle: 90,
                        width: 10,
                        height: 10,
                        fill: '#000',
                    }),
                ], {
                    left: center.left,
                    top: center.top,
                    originX: 'center',
                    originY: 'center',
                });
                canvas.add(arrow);
                canvas.setActiveObject(arrow);
                break;

            default:
                canvas.isDrawingMode = false;
        }

        canvas.renderAll();
    };

    const undo = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const objects = canvas.getObjects();
        if (objects.length > 0) {
            canvas.remove(objects[objects.length - 1]);
            canvas.renderAll();
        }
    };

    const clear = () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const bg = canvas.backgroundImage;
        canvas.clear();
        if (bg) {
            canvas.set("backgroundImage", bg);
            canvas.renderAll();
        }
    };

    const exportAnnotatedImage = () => {
        const canvas = fabricCanvasRef.current;
        return canvas?.toDataURL({ format: 'png', multiplier: 1 }) || '';
    };

    const dataURLtoBlob = (dataurl) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
        return new Blob([u8arr], { type: mime });
    };

    const sendToDeveloper = async () => {
        setIsSubmitting(true);
        const image = exportAnnotatedImage();
        const formData = new FormData();
        formData.append('screenshot', dataURLtoBlob(image), 'screenshot.png');
        formData.append('title', title);
        formData.append('description', description);
        formData.append('userAgent', navigator.userAgent);
        formData.append('url', window.location.href);
        formData.append('timestamp', new Date().toISOString());

        if (attachment.length > 0) {
            attachment.forEach(file => {
                formData.append('attachment[]', file);
            });
        }

        try {
            await fetch(apiEndpoint, {
                method: 'POST',
                body: formData,
            });
            setIsSubmitting(false);
            Swal.fire('Feedback sent to developer!', '', 'success');
            handleClose();
        } catch (err) {
            setIsSubmitting(false);
            console.error('Failed to send feedback:', err);
            Swal.fire('Failed to send feedback.', '', 'error');
        }
    };

    const changeColor = (color) => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        const activeObject = canvas.getActiveObject();
        if (!activeObject) return;

        if (activeObject.type === 'group') {
            activeObject._objects.forEach((obj) => {
                if (obj.set) {
                    obj.set('stroke', color);
                    obj.set('fill', color);
                }
            });
        } else {
            if (activeObject.set) {
                if ('fill' in activeObject) activeObject.set('fill', color);
                if ('stroke' in activeObject) activeObject.set('stroke', color);
            }
        }

        canvas.renderAll();
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        clear();
        setTitle('');
        setDescription('');
        setAttachment([]);
        setScreenshotReady(false);
    };

    useEffect(() => {
        // Only proceed if the modal is open AND the screenshot process has not completed yet
        if (!open || screenshotReady) return;

        let attempts = 0;
        const maxAttempts = 10;
        let timerId;

        const waitForDOMAndInitialize = () => {
            const container = containerRef.current;
            const canvas = canvasRef.current;

            if (
                container &&
                canvas &&
                container.getBoundingClientRect().width > 0 &&
                container.getBoundingClientRect().height > 0
            ) {
                clearTimeout(timerId); // Stop any pending retries

                const initialized = initializeCanvas();
                if (initialized) {
                    // Only proceed to screenshot if Canvas initialized successfully
                    captureScreenshot();
                } else if (attempts < maxAttempts) {
                    // Retry if initialization failed but is within bounds and attempts remain
                    attempts++;
                    timerId = setTimeout(waitForDOMAndInitialize, 200);
                } else {
                    console.error('Canvas failed to initialize after max attempts.');
                }
            } else {
                attempts++;
                if (attempts < maxAttempts) {
                    // Retry if DOM elements are not ready or have zero dimensions
                    timerId = setTimeout(waitForDOMAndInitialize, 200);
                } else {
                    console.error('Canvas container did not become ready in time (dimensions zero).');
                }
            }
        };

        // Only start the process if 'open' is true and 'screenshotReady' is false
        if (open && !screenshotReady) {
            waitForDOMAndInitialize();
        }

        return () => {
            clearTimeout(timerId);
            // Cleanup: Dispose of fabric canvas when the modal closes (open becomes false)
            // This runs when 'open' changes from true to false, or on unmount.
            // It does NOT run when 'screenshotReady' changes from false to true because 
            // 'open' is still true.
            if (!open && fabricCanvasRef.current) {
                fabricCanvasRef.current.dispose();
                fabricCanvasRef.current = null;
            }
        };
    }, [open, initializeCanvas, captureScreenshot]);

    return (
        <>
            <button
                className="feedback-btn"
                onClick={handleOpen}
                aria-label="Open feedback widget"
            >
                <span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: iconSVG.feedback }} />
                Feedback
            </button>

            {open && (
                <div className="feedback-modal-overlay" onClick={handleClose}>
                    <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="feedback-container">
                            <div className="feedback-canvas-section">
                                <div ref={containerRef} className="feedback-canvas-wrapper">
                                    <canvas ref={canvasRef} />
                                </div>
                                {
                                    !screenshotReady ? (
                                        <div className="feedback-spinner-overlay">
                                            <div className="feedback-spinner" />
                                            <p>{"Capturing screenshot..."}</p>
                                        </div>
                                    ) : null
                                }
                            </div>

                            <div className="feedback-form-section">
                                <div className="feedback-header align-items-baseline">
                                    <h2>Help Us Improve. <br /> <br /> Navigation, bugs, or ideas - your input helps us make your experience better.</h2>
                                    <button className="feedback-close-btn" onClick={handleClose} aria-label="Close">
                                        <div dangerouslySetInnerHTML={{ __html: iconSVG.close }} />
                                    </button>
                                </div>

                                {!screenshotReady ? (
                                    <p className="feedback-loading">Capturing screenshot...</p>
                                ) : (
                                    <>
                                        <div className="feedback-toolbar">
                                            <button
                                                className="feedback-tool-btn"
                                                onClick={() => setTool('text')}
                                                title="Add text"
                                            >
                                                <div dangerouslySetInnerHTML={{ __html: iconSVG.text }} />
                                            </button>
                                            <button
                                                className="feedback-tool-btn"
                                                onClick={() => setTool('arrow')}
                                                title="Add arrow"
                                            >
                                                <div dangerouslySetInnerHTML={{ __html: iconSVG.arrow }} />
                                            </button>
                                            <button
                                                className="feedback-tool-btn"
                                                onClick={undo}
                                                title="Undo"
                                            >
                                                <div dangerouslySetInnerHTML={{ __html: iconSVG.undo }} />
                                            </button>
                                            <button
                                                className="feedback-tool-btn feedback-delete-btn"
                                                onClick={clear}
                                                title="Clear"
                                            >
                                                <div dangerouslySetInnerHTML={{ __html: iconSVG.delete }} />
                                            </button>
                                            <input
                                                type="color"
                                                className="feedback-color-picker"
                                                onChange={(e) => changeColor(e.target.value)}
                                                title="Change color"
                                            />
                                        </div>
                                        <div style={{ width: '100%' }}>
                                            <input
                                                id='ele-feedback-input'
                                                type="text"
                                                className="feedback-input"
                                                placeholder="Bug Title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                            />
                                            <textarea
                                                id='ele-feedback-textarea'
                                                className="feedback-textarea"
                                                placeholder="Description"
                                                rows="4"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                            />
                                        </div>

                                        <label className="feedback-upload-label">
                                            <div dangerouslySetInnerHTML={{ __html: iconSVG.upload }} />
                                            Upload Attachment
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                hidden
                                                onChange={(e) => setAttachment(Array.from(e.target.files))}
                                            />
                                        </label>

                                        {attachment.length > 0 && (
                                            <div className="feedback-attachments">
                                                {attachment.map((file, index) => (
                                                    <>
                                                        <p key={index} className="feedback-attachment-item">
                                                            Attached: {file.name}
                                                        </p>
                                                        <img
                                                            src="/close.svg"
                                                            height={25}
                                                            onClick={() => {
                                                                setAttachment([]);
                                                                if (fileInputRef.current) {
                                                                    fileInputRef.current.value = "";
                                                                }
                                                            }}
                                                            alt="close button"
                                                        />
                                                    </>
                                                ))}
                                            </div>
                                        )}

                                        <button
                                            className="feedback-submit-btn"
                                            onClick={sendToDeveloper}
                                            disabled={!title || !description || isSubmitting}
                                        >
                                            Send to Developer
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {isSubmitting && (
                            <div className="feedback-spinner-overlay">
                                <div className="feedback-spinner" />
                                <p>{"Capturing screenshot..."}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}